// File: src/pages/Inventory.tsx
// ✅ REDESIGN 2025-11-01: Visual improvements to match dashboard branding
// CHANGES: Removed stat cards, added Damaged/Maintenance counts, changed green buttons to blue

import React, { useState, useEffect, useRef } from 'react';
import { Package, Search, Filter, Eye, Edit, History, Camera, AlertCircle, Loader2, UserCheck, User, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// AddItemModal removed - inventory items are created through Purchases workflow
import EditItemModal from '../components/inventory/EditItemModal';
import ItemDetailModal from '../components/inventory/ItemDetailModal';
import { AssignItemModal } from '../components/inventory/AssignItemModal';
// BulkInventoryUploadModal removed - inventory items are created through Purchases workflow
import { inventoryAPI, InventoryItem as APIInventoryItem, UpdateItemRequest } from '../services/inventoryAPI';
import { categoryAPI } from '../services/categoryAPI';
import { InventoryCategory } from '../types/category';
import { useAssignments } from '../hooks/useAssignments';
import { signOut } from 'aws-amplify/auth';
// BulkUploadResult import removed - bulk upload removed from inventory page

// 📊 Centralized logging
import { logger } from '../utils/logger';

// Create service-specific logger
const inventoryPageLogger = logger.createServiceLogger('InventoryPage');

const Inventory: React.FC = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<'all' | 'serialized' | 'bulk' | 'body-armor'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all');
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [availableSubcategories, setAvailableSubcategories] = useState<InventoryCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
 // isAddModalOpen removed - inventory items created through Purchases workflow
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
 const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  // isBulkUploadModalOpen removed - inventory items created through Purchases workflow
  const [selectedItem, setSelectedItem] = useState<APIInventoryItem | null>(null);
  const [inventory, setInventory] = useState<APIInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // addingItem removed - inventory items created through Purchases workflow
  const [updatingItem, setUpdatingItem] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  // ✅ NEW: Auth error handling
  const [authError, setAuthError] = useState<boolean>(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // ✅ NEW: Ref to prevent double-loading
  const hasLoadedOnce = useRef(false);

  // ✅ NEW: Status filter state (multi-select)
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set(['Available']));

  // Assignment hook
  const {
    assignItem,
    getItemHistory,
    error: assignmentError,
    clearError
  } = useAssignments();

  // Fallback categories for when API fails
  const fallbackCategories = ['All', 'Firearms', 'Electronics', 'Protective Gear', 'Vehicles', 'Medical', 'Communications', 'Tools', 'Uniforms', 'Training Equipment'];

  // Available status options for filtering
  const statusOptions = ['Available', 'Assigned', 'Damaged', 'Lost', 'Retired', 'Maintenance'];

  // ✅ NEW: Calculate counts for Damaged and Maintenance
  const damagedCount = inventory.filter(item => (item as any).status === 'Damaged').length;
  const maintenanceCount = inventory.filter(item => (item as any).status === 'Maintenance').length;

  // ✅ IMPROVED: Load inventory items with better error handling
  const loadInventoryItems = async () => {
    if (authError) {
      inventoryPageLogger.warn('Skipping inventory load due to auth error');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await inventoryAPI.getItems();
      
      if (response.success && response.data) {
        setInventory(response.data);
        inventoryPageLogger.info('Inventory loaded successfully', { itemCount: response.data.length });
      } else {
        if (response.error?.code === 'NO_SESSION' || response.error?.code === 'AUTH_ERROR') {
          inventoryPageLogger.error('Authentication error detected', response.error);
          setAuthError(true);
          setError('Your session has expired. Please log in again.');
        } else {
          setError(response.error?.message || 'Failed to load inventory');
        }
      }
    } catch (err: any) {
      inventoryPageLogger.error('Exception while loading inventory', undefined, err);
      
      if (err.code === 'NO_SESSION' || err.code === 'CIRCUIT_BREAKER' || err.message?.includes('log in')) {
        setAuthError(true);
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load inventory');
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ IMPROVED: Load categories with auth error handling
  const loadCategories = async () => {
    if (authError) return;

    setLoadingCategories(true);
    try {
      inventoryPageLogger.debug('Loading categories for inventory filtering');
      const response = await categoryAPI.getAllCategories();
      
      if (response.success && response.data) {
        const tree = categoryAPI.buildCategoryTree(response.data.categories);
        const categoriesWithChildren: InventoryCategory[] = tree.map(node => ({
          categoryId: node.categoryId,
          name: node.name,
          description: node.description,
          icon: node.icon,
          sortOrder: node.sortOrder,
          isActive: node.isActive,
          itemCount: node.itemCount,
          parentCategoryId: node.parentCategoryId,
          children: node.children.map(child => ({
            categoryId: child.categoryId,
            name: child.name,
            description: child.description,
            icon: child.icon,
            sortOrder: child.sortOrder,
            isActive: child.isActive,
            itemCount: child.itemCount,
            parentCategoryId: child.parentCategoryId
          }))
        }));
        
        inventoryPageLogger.info('Categories loaded for filtering', { count: categoriesWithChildren.length });
        setCategories(categoriesWithChildren);
      } else {
        if (response.error?.code === 'NO_SESSION' || response.error?.code === 'AUTH_ERROR') {
          inventoryPageLogger.error('Authentication error in categories', response.error);
          setAuthError(true);
        } else {
          inventoryPageLogger.error('Failed to load categories', { error: response.error });
          inventoryPageLogger.debug('Using fallback categories for filtering');
        }
      }
    } catch (error) {
      inventoryPageLogger.error('Error loading categories', undefined, error as Error);
      
      if ((error as any).code === 'NO_SESSION' || (error as any).code === 'CIRCUIT_BREAKER') {
        setAuthError(true);
      }
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    if (!hasLoadedOnce.current) {
      hasLoadedOnce.current = true;
      loadInventoryItems();
      loadCategories();
    }
  }, []);

  useEffect(() => {
    if (assignmentError) {
      showNotification('error', assignmentError);
      clearError();
    }
  }, [assignmentError]);

  useEffect(() => {
    updateSubcategories(selectedCategory);
  }, [selectedCategory, categories]);

  useEffect(() => {
    if (authError && !loggingOut) {
      inventoryPageLogger.warn('Auth error detected, will auto-logout in 5 seconds');
      const timer = setTimeout(async () => {
        await handleAuthErrorLogout();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [authError, loggingOut]);

  const handleAuthErrorLogout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      inventoryPageLogger.error('Error during auth error logout', undefined, error as Error);
      window.location.href = '/';
    }
  };

  const updateSubcategories = (categoryId: string) => {
    if (categoryId === 'all' || categories.length === 0) {
      setAvailableSubcategories([]);
      setSelectedSubcategory('all');
      return;
    }

    const parentCategory = categories.find(cat => cat.categoryId === categoryId);
    const subcategories = parentCategory?.children || [];
    
    setAvailableSubcategories(subcategories);
    setSelectedSubcategory('all');
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

 // handleAddItem removed - inventory items created through Purchases workflow

  const handleViewItem = (item: APIInventoryItem) => {
    setSelectedItem(item);
    setIsDetailModalOpen(true);
  };

  const handleEditItem = (item: APIInventoryItem) => {
    setSelectedItem(item);
    setIsDetailModalOpen(false);
    setIsEditModalOpen(true);
  };

  const handleUpdateItem = async (itemId: string, itemData: UpdateItemRequest) => {
    setUpdatingItem(true);
    
    try {
      const response = await inventoryAPI.updateItem(itemId, itemData);
      
      if (response.success && response.data) {
        setInventory(prev => prev.map(item => 
          item.itemId === itemId ? response.data! : item
        ));
        setIsEditModalOpen(false);
        setSelectedItem(null);
        showNotification('success', 'Item updated successfully');
      } else {
        showNotification('error', `Failed to update item: ${response.error?.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      showNotification('error', `Failed to update item: ${err.message}`);
    } finally {
      setUpdatingItem(false);
    }
  };

  const handleDeleteItem = async (item: APIInventoryItem) => {
    try {
      const response = await inventoryAPI.deleteItem(item.itemId);
      
      if (response.success) {
        setInventory(prev => prev.filter(i => i.itemId !== item.itemId));
        setIsDetailModalOpen(false);
        setSelectedItem(null);
        showNotification('success', 'Item deleted successfully');
      } else {
        showNotification('error', `Failed to delete item: ${response.error?.message || 'Unknown error'}`);
      }
    } catch (err: any) {
      showNotification('error', `Failed to delete item: ${err.message}`);
    }
  };

  const handleAssignItem = (item: APIInventoryItem) => {
    setSelectedItem(item);
    setIsAssignModalOpen(true);
  };

  const handleAssignmentSubmit = async (assignmentData: any) => {
    try {
      if (!selectedItem) return;
      
      await assignItem(selectedItem, assignmentData);
      showNotification('success', 'Item assigned successfully');
      setIsAssignModalOpen(false);
      await loadInventoryItems();
    } catch (err) {
      showNotification('error', err instanceof Error ? err.message : 'Failed to assign item');
    }
  };

  const handleViewAssignedUser = (item: APIInventoryItem) => {
    handleViewItem(item);
  };

  const canAssignItem = (item: APIInventoryItem): boolean => {
    if (item.itemType === 'SERIALIZED') {
      return (item as any).status === 'Available';
    } else {
      return (item as any).availableQuantity > 0;
    }
  };

  const handleStatusFilterToggle = (status: string) => {
    const newFilters = new Set(statusFilters);
    if (newFilters.has(status)) {
      newFilters.delete(status);
    } else {
      newFilters.add(status);
    }
    setStatusFilters(newFilters);
  };

  const handleShowAllStatuses = () => {
    if (statusFilters.size === statusOptions.length) {
      setStatusFilters(new Set(['Available']));
    } else {
      setStatusFilters(new Set(statusOptions));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Available': return 'text-green-700 bg-green-100';
      case 'Assigned': return 'text-blue-700 bg-blue-100';
      case 'Maintenance': return 'text-yellow-700 bg-yellow-100';
      case 'Damaged': return 'text-orange-700 bg-orange-100';
      case 'Lost': return 'text-red-700 bg-red-100';
      case 'Retired': return 'text-gray-700 bg-gray-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'Excellent': return 'text-green-700';
      case 'Good': return 'text-blue-700';
      case 'Fair': return 'text-yellow-700';
      case 'Poor': return 'text-orange-700';
      case 'Needs Repair': return 'text-red-700';
      default: return 'text-gray-700';
    }
  };

  // ✅ PHASE 1 REFACTOR: Match by ID or name (supports legacy items with name strings like "uniforms")
  const findCategoryById = (categoryIdOrName: string): InventoryCategory | undefined => {
    if (!categoryIdOrName || categories.length === 0) return undefined;
    
    for (const category of categories) {
      // Match by ID or name (case-insensitive for name matching)
      if (category.categoryId === categoryIdOrName || 
          category.name.toLowerCase() === categoryIdOrName.toLowerCase()) {
        return category;
      }
      if (category.children) {
        const child = category.children.find((c: InventoryCategory) => 
          c.categoryId === categoryIdOrName || 
          c.name.toLowerCase() === categoryIdOrName.toLowerCase()
        );
        if (child) return child;
      }
    }
    return undefined;
  };

  const getCategoryNameById = (categoryId: string): string => {
    if (categoryId === 'all') return 'All';
    
    const category = findCategoryById(categoryId);
    if (category) return category.name;
    
    const fallbackMap: Record<string, string> = {
      'cat_firearms': 'Firearms',
      'cat_protective_equipment': 'Protective Gear',
      'cat_electronics': 'Electronics',
      'cat_communication': 'Communications',
      'cat_tools': 'Tools',
      'cat_uniform_items': 'Uniforms',
      'cat_vehicle_equipment': 'Vehicles',
      'cat_medical': 'Medical',
      'cat_training': 'Training Equipment'
    };
    
    return fallbackMap[categoryId] || categoryId;
  };

  const searchInHierarchicalCategories = (item: any, searchTerm: string): boolean => {
    if (!item.categoryId && !item.parentCategoryId) return false;
    
    const category = findCategoryById(item.categoryId);
    const parentCategory = findCategoryById(item.parentCategoryId);
    
    return (category?.name.toLowerCase().includes(searchTerm) || false) ||
           (parentCategory?.name.toLowerCase().includes(searchTerm) || false);
  };

  const matchesHierarchicalCategory = (item: any, categoryId: string): boolean => {
    if (categories.length === 0) {
      return item.category === getCategoryNameById(categoryId);
    }
    
    return item.parentCategoryId === categoryId || 
           item.categoryId === categoryId ||
           item.category === getCategoryNameById(categoryId);
  };

  const matchesHierarchicalSubcategory = (item: any, subcategoryId: string): boolean => {
    if (categories.length === 0) return true;
    
    return item.categoryId === subcategoryId;
  };

  const filteredItems = inventory.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (item.name?.toLowerCase() || '').includes(searchLower) ||
                         (item.itemType === 'SERIALIZED' && ((item as any).serialNumber?.toLowerCase() || '').includes(searchLower)) ||
                         (item.category?.toLowerCase() || '').includes(searchLower) ||
                         (categories.length > 0 && searchInHierarchicalCategories(item, searchLower));
    
    const matchesCategory = selectedCategory === 'all' || matchesHierarchicalCategory(item, selectedCategory);
    const matchesSubcategory = selectedSubcategory === 'all' || matchesHierarchicalSubcategory(item, selectedSubcategory);
    
    const matchesType = activeFilter === 'all' || 
                       (activeFilter === 'serialized' && item.itemType === 'SERIALIZED') ||
                       (activeFilter === 'bulk' && (item.itemType as string) !== 'SERIALIZED' && !('bodyArmor' in item)) ||
                       (activeFilter === 'body-armor' && 'bodyArmor' in item);
    
    const itemStatus = (item as any).status || 'Available';
    const matchesStatus = statusFilters.size === 0 || statusFilters.has(itemStatus);
    
    return matchesSearch && matchesCategory && matchesSubcategory && matchesType && matchesStatus;
  });

  if (authError) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-900 mb-2">Session Expired</h2>
          <p className="text-red-700 mb-6">
            Your session has expired. You will be logged out automatically in a few seconds.
          </p>
          <button
            onClick={handleAuthErrorLogout}
            disabled={loggingOut}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center mx-auto"
          >
            {loggingOut ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Logging out...
              </>
            ) : (
              <>
                <LogOut className="w-5 h-5 mr-2" />
                Log Out Now
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Inventory Management</h2>
          <p className="text-gray-600 mt-1">Manage department equipment and assets</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => navigate('/purchases')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center transition-colors shadow-md"
          >
            Record Purchase to Add Items
          </button>
        </div>
      </div>
      {/* Notification */}
      {notification && (
        <div className={`p-4 rounded-md ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          <div className="flex justify-between items-center">
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="text-current hover:opacity-75"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && !authError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error Loading Inventory</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              {!authError && (
                <button 
                  onClick={loadInventoryItems}
                  className="text-sm text-red-800 underline hover:no-underline mt-2"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Inventory</h3>
          <p className="text-gray-600">Fetching your department's equipment...</p>
        </div>
      ) : (
        <>
          {/* Enhanced Filters and Search - NO STAT CARDS */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative max-w-sm">
                <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-blue-500"
                />
              </div>
              
              {/* Category Filter */}
              <div className="min-w-0">
                {loadingCategories ? (
                  <div className="px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-sm">
                    Loading...
                  </div>
                ) : categories.length > 0 ? (
                  <select
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-blue-500 min-w-32"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(category => (
                      <option key={category.categoryId} value={category.categoryId}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-blue-500 min-w-32"
                  >
                    {fallbackCategories.map(category => (
                      <option key={category} value={category.toLowerCase()}>{category}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Subcategory Filter */}
              <div className="min-w-0">
                <select
                  value={selectedSubcategory}
                  onChange={(e) => setSelectedSubcategory(e.target.value)}
                  disabled={availableSubcategories.length === 0}
                  className={`px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-blue-500 min-w-32 ${
                    availableSubcategories.length === 0 ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="all">
                    {availableSubcategories.length === 0 ? 'No Subcategories' : 'All Subcategories'}
                  </option>
                  {availableSubcategories.map(subcategory => (
                    <option key={subcategory.categoryId} value={subcategory.categoryId}>
                      {subcategory.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'serialized', label: 'Serialized' },
                  { key: 'bulk', label: 'Bulk' },
                  { key: 'body-armor', label: 'Body Armor' }
                ].map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setActiveFilter(filter.key as any)}
                    className={`px-3 py-1 rounded-md text-sm transition-colors ${
                      activeFilter === filter.key
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Status Filter Checkboxes with Damaged and Maintenance counts */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Status Filters:</label>
                <button
                  onClick={handleShowAllStatuses}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  {statusFilters.size === statusOptions.length ? 'Reset to Available' : 'Show All'}
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {statusOptions.map((status) => (
                  <label
                    key={status}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={statusFilters.has(status)}
                      onChange={() => handleStatusFilterToggle(status)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      {status}
                      {status === 'Damaged' && damagedCount > 0 && (
                        <span className="ml-1 text-orange-600 font-medium">({damagedCount})</span>
                      )}
                      {status === 'Maintenance' && maintenanceCount > 0 && (
                        <span className="ml-1 text-yellow-600 font-medium">({maintenanceCount})</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Filter summary */}
            {(searchTerm || selectedCategory !== 'all' || selectedSubcategory !== 'all' || activeFilter !== 'all' || statusFilters.size !== 1 || !statusFilters.has('Available')) && (
              <div className="mt-3 text-sm text-gray-600">
                Showing {filteredItems.length} of {inventory.length} items
                {searchTerm && <span> matching "{searchTerm}"</span>}
                {selectedCategory !== 'all' && <span> in {getCategoryNameById(selectedCategory)}</span>}
                {selectedSubcategory !== 'all' && <span> → {getCategoryNameById(selectedSubcategory)}</span>}
                {statusFilters.size > 0 && (
                  <span> • Status: {Array.from(statusFilters).join(', ')}</span>
                )}
              </div>
            )}
          </div>

          {/* Inventory Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <div key={item.itemId} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                {/* Item Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <Package className="w-5 h-5 text-blue-600 mr-2" />
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.name || 'Unnamed Item'}</h3>
                      <p className="text-sm text-gray-600">
                        {(() => {
                          const itemCategory = findCategoryById((item as any).categoryId);
                          const itemParent = findCategoryById((item as any).parentCategoryId);
                          
                          if (itemParent && itemCategory && itemCategory.categoryId !== itemParent.categoryId) {
                            return `${itemParent.name} › ${itemCategory.name}`;
                          } else if (itemCategory) {
                            return itemCategory.name;
                          } else {
                            return item.category || 'Uncategorized';
                          }
                        })()}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button 
                      onClick={() => handleViewItem(item)}
                      className="p-1 text-gray-400 hover:text-gray-600" 
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => handleEditItem(item)}
                      className="p-1 text-gray-400 hover:text-gray-600" 
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="p-1 text-gray-400 hover:text-gray-600" title="History">
                      <History className="w-4 h-4" />
                    </button>
                    <button className="p-1 text-gray-400 hover:text-gray-600" title="Photos">
                      <Camera className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Item Details */}
                <div className="space-y-2 text-sm">
                  {item.itemType === 'SERIALIZED' && (item as any).serialNumber && (
                    <div>
                      <span className="font-medium">Serial:</span> {(item as any).serialNumber}
                    </div>
                  )}
                  
                  {(item.itemType as string) !== 'SERIALIZED' && (
                    <div>
                      <span className="font-medium">Quantity:</span> {(item as any).availableQuantity}/{(item as any).totalQuantity} available
                    </div>
                  )}

                  <div>
                    <span className="font-medium">Location:</span> {item.location || 'Not specified'}
                    {item.subLocation && (
                      <span className="text-gray-500"> → {item.subLocation}</span>
                    )}
                  </div>

                  <div>
                    <span className="font-medium">Condition:</span> 
                    <span className={`ml-1 ${getConditionColor(item.condition || 'Unknown')}`}>
                      {item.condition || 'Unknown'}
                    </span>
                  </div>

                  {item.itemType === 'SERIALIZED' && (item as any).status && (
                    <div>
                      <span className="font-medium">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor((item as any).status)}`}>
                        {(item as any).status}
                      </span>
                    </div>
                  )}

                  {/* Assigned To Badge */}
                  {item.itemType === 'SERIALIZED' && (item as any).assignedTo && (item as any).assignedToName && (
                    <div className="pt-2 border-t border-gray-100">
                      <button
                        onClick={() => handleViewAssignedUser(item)}
                        className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        <span className="font-medium">Assigned to: {(item as any).assignedToName}</span>
                      </button>
                    </div>
                  )}

                  {'bodyArmor' in item && (item as any).bodyArmor && (
                    <div className="pt-2 border-t border-gray-100">
                      <div><span className="font-medium">Level:</span> {(item as any).bodyArmor.ballisticLevel}</div>
                      <div><span className="font-medium">Size:</span> {(item as any).bodyArmor.size}</div>
                      <div><span className="font-medium">Expires:</span> {(item as any).bodyArmor.expirationDate}</div>
                    </div>
                  )}

                  {/* Audit Info */}
                  <div className="pt-2 border-t border-gray-100 text-xs text-gray-500">
                    <div>Updated: {new Date(item.updatedAt).toLocaleDateString()} by {item.updatedBy}</div>
                  </div>
                </div>

                {/* Assignment Action Button - Changed to Blue */}
                <div className="mt-4 flex space-x-2">
                  {canAssignItem(item) && (
                    <button
                      onClick={() => handleAssignItem(item)}
                      className="flex-1 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center shadow-sm"
                      title="Assign Item"
                    >
                      <UserCheck className="w-3 h-3 mr-1" />
                      Assign
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && !loading && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {inventory.length === 0 ? 'No inventory items yet' : 'No items found'}
              </h3>
              <p className="text-gray-600">
                {inventory.length === 0 
                  ? 'Record a purchase to add inventory items.' 
                  : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          )}
        </>
      )}

     {/* Add Item Modal removed - inventory items created through Purchases workflow */}

      {/* Edit Item Modal */}
      <EditItemModal
        isOpen={isEditModalOpen}
        item={selectedItem}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedItem(null);
        }}
        onSave={handleUpdateItem}
      />

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={isDetailModalOpen}
        item={selectedItem}
        onClose={() => setIsDetailModalOpen(false)}
        onEdit={handleEditItem}
        onDelete={handleDeleteItem}
      />

      {/* Assignment Modal */}
      <AssignItemModal
        isOpen={isAssignModalOpen}
        item={selectedItem}
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedItem(null);
        }}
        onAssign={handleAssignmentSubmit}
      />

     {/* Bulk Upload Modal removed - inventory items created through Purchases workflow */}
    </div>
  );
};

export default Inventory;